if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Returns selected add-ons in Shopify's bundled cart/add format.
     * @returns {Array}
     */
    getSelectedAddonItems() {
      if (!this.form.classList.contains('js-product-form-main')) return [];

      const productSection = this.closest('.js-product') || document;
      const addonWrapper = productSection.querySelector('[data-addon-products]');
      if (!addonWrapper) return [];

      return Array.from(addonWrapper.querySelectorAll('[data-addon-input]:checked'))
        .map((input) => ({
          id: Number(input.value),
          quantity: 1
        }))
        .filter((item) => Number.isInteger(item.id) && item.id > 0);
    }

    /**
     * Builds the parent line item from the product form.
     * @param {FormData} formData - Submitted product form data.
     * @param {string} [freeGiftVariantId] - Free gift variant ID from line item properties.
     * @returns {object}
     */
    getMainItem(formData, freeGiftVariantId) {
      const mainItem = {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity') || 1)
      };
      const properties = {};

      formData.forEach((value, key) => {
        if (!key.startsWith('properties[')) return;

        const propName = key.replace('properties[', '').replace(']', '');
        if (value !== '') properties[propName] = value;
      });

      if (freeGiftVariantId) properties._free_gift_variant_id = freeGiftVariantId;
      if (Object.keys(properties).length > 0) mainItem.properties = properties;

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) mainItem.selling_plan = sellingPlan;

      return mainItem;
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const isBuyNow = evt.submitter && evt.submitter.classList.contains('js-custom-buy-it-now');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      const freeGiftVariantId = formData.get('properties[_free_gift_variant_id]');
      const addonItems = this.getSelectedAddonItems();
      let fetchRequestOpts;

      if (freeGiftVariantId || addonItems.length > 0) {
        const mainItem = this.getMainItem(formData, freeGiftVariantId);
        const items = [mainItem];

        if (freeGiftVariantId) {
          items.push({
            id: Number(freeGiftVariantId),
            quantity: Number(formData.get('quantity') || 1),
            properties: { _is_free_gift: 'true' }
          });
        }

        items.push(...addonItems);

        fetchRequestOpts = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        };
      } else {
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);
        fetchRequestOpts = {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        };
      }

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        const mainVariantId = Number(formData.get('id'));
        const addedMainItem = Array.isArray(data.items)
          ? data.items.find((item) => item.variant_id === mainVariantId) || data.items[0]
          : data;
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);

          if (isBuyNow) {
            window.location.href = '/checkout';
            return;
          }
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === mainVariantId
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: mainVariantId,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === addedMainItem.quantity)
                ? itemInOldCart.quantity : addedMainItem.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: mainVariantId
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMessage
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Returns the preferred inventory contact destination and label.
     * @param {ProductForm} productForm - Product form requesting contact details.
     * @returns {{ url: string, label: string }} Contact details.
     */
    static getInventoryContact(productForm) {
      const whatsappLink = document.querySelector(
        '.secondary-nav__cta[href^="https://wa.me/"], .secondary-nav__cta[href^="https://api.whatsapp.com/"]'
      );

      return {
        url: whatsappLink?.href
          || productForm.dataset.inventoryContactUrl
          || theme.settings.inventoryContactUrl
          || '/pages/contact-us',
        label: productForm.dataset.inventoryContactLabel
          || theme.strings.inventoryContactLabel
          || 'Contact us for more.'
      };
    }

    /**
     * Shows one reusable inventory-limit toast and restarts its timeout.
     * @param {string} message - Inventory-limit message.
     * @param {string} contactUrl - Contact destination.
     * @param {string} contactLabel - Contact link label.
     */
    static showInventoryLimitToast(message, contactUrl, contactLabel) {
      let toast = document.querySelector('.js-inventory-limit-toast');

      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'inventory-limit-toast js-inventory-limit-toast';
        toast.hidden = true;
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.setAttribute('aria-atomic', 'true');

        const content = document.createElement('div');
        content.className = 'inventory-limit-toast__content';

        const messageElement = document.createElement('div');
        messageElement.className = 'inventory-limit-toast__message';

        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'inventory-limit-toast__close';
        closeButton.setAttribute('aria-label', 'Close');
        closeButton.textContent = '×';
        closeButton.addEventListener('click', () => ProductForm.hideInventoryLimitToast());

        content.append(messageElement, closeButton);
        toast.append(content);
        document.body.append(toast);
      }

      clearTimeout(ProductForm.inventoryLimitToastTimer);
      clearTimeout(ProductForm.inventoryLimitToastHideTimer);

      const messageElement = toast.querySelector('.inventory-limit-toast__message');
      const normalizedMessage = message.trim().endsWith('.') ? message.trim() : `${message.trim()}.`;
      messageElement.replaceChildren(document.createTextNode(`${normalizedMessage} `));

      if (contactUrl && contactLabel) {
        const contactLink = document.createElement('a');
        contactLink.href = contactUrl;
        contactLink.className = 'inventory-limit-toast__link';
        contactLink.textContent = contactLabel;
        messageElement.append(contactLink);
      }

      toast.hidden = false;
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(() => toast.classList.add('is-visible'));
      } else {
        toast.classList.add('is-visible');
      }
      ProductForm.inventoryLimitToastTimer = setTimeout(
        () => ProductForm.hideInventoryLimitToast(),
        4000
      );
    }

    /**
     * Hides the inventory-limit toast.
     */
    static hideInventoryLimitToast() {
      const toast = document.querySelector('.js-inventory-limit-toast');
      if (!toast) return;

      clearTimeout(ProductForm.inventoryLimitToastTimer);
      clearTimeout(ProductForm.inventoryLimitToastHideTimer);
      toast.classList.remove('is-visible');
      ProductForm.inventoryLimitToastHideTimer = setTimeout(() => {
        if (!toast.classList.contains('is-visible')) toast.hidden = true;
      }, 200);
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMessage = Array.isArray(error) ? error.join('\n') : error || '';
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      const isProductPage = this.dataset.productFormContext === 'product-page'
        && !this.closest('quick-add-drawer');
      const isMaxQuantityError = (message) => typeof message === 'string'
        && /^The maximum quantity of this item is already in your cart\.?$/i.test(message.trim());
      const errors = error ? (Array.isArray(error) ? error : [error]) : [];
      const visibleErrors = errors.filter((message) => isProductPage || !isMaxQuantityError(message));
      const maxQuantityError = errors.find(isMaxQuantityError);

      if (!isProductPage && maxQuantityError) {
        const contact = ProductForm.getInventoryContact(this);
        ProductForm.showInventoryLimitToast(maxQuantityError, contact.url, contact.label);
      }

      this.errorMsg.replaceChildren();
      this.errorMsg.hidden = visibleErrors.length === 0;
      visibleErrors.forEach((message, index) => {
        if (index > 0) this.errorMsg.append(document.createElement('br'));
        this.errorMsg.append(document.createTextNode(message));

        if (isProductPage && isMaxQuantityError(message)) {
          const contact = ProductForm.getInventoryContact(this);
          if (contact.url && contact.label) {
            const contactLink = document.createElement('a');
            contactLink.href = contact.url;
            contactLink.className = 'link';
            contactLink.textContent = contact.label;
            this.errorMsg.append(document.createTextNode(' '), contactLink);
          }
        }
      });
    }
  }

  customElements.define('product-form', ProductForm);
}
